import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./AddEditUser.css";
import { userAPI } from "../services/api";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { useUserManagementFilters } from "../hooks/useUserManagementFilters";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRoutePermissions } from "../utils/auth";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";
import {
  formatUserGeoColumnDisplay,
  formatUserTerritoryColumnDisplay,
} from "../utils/userTerritoryScope";
import { MASTER_USER_EXCEL_HEADERS } from "../utils/userCategoryGeoValidation";

// users will be fetched from API

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatGeoTooltipHtml = (value) => {
  const text = String(value ?? "").trim();
  if (!text || text === "-") return escapeHtml(text || "-");
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return escapeHtml(text);
  return parts.map((part) => escapeHtml(part)).join("<br>");
};

const renderGeoTooltipCell = (displayValue, type) => {
  const text = displayValue == null || displayValue === "" ? "-" : String(displayValue);
  if (type !== "display") return text;
  const display = escapeHtml(text);
  const tooltipHtml = formatGeoTooltipHtml(text);
  return `<span class="user-geo-tooltip-cell user-geo-tip"
    data-bs-toggle="tooltip"
    data-bs-placement="top"
    data-bs-html="true"
    data-bs-title="${tooltipHtml}"
    title="${display}">${display}</span>`;
};

const initUserGeoTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip) return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].user-geo-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
    new window.bootstrap.Tooltip(el, {
      html: true,
      placement: "top",
      container: "body",
      customClass: "user-geo-cell-tooltip",
      trigger: "hover focus",
    });
  });
};

const disposeUserGeoTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip) return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].user-geo-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
  });
};

const NA_DISPLAY = "N/A";

const isBlankOrNa = (value) => {
  const text = String(value ?? "").trim();
  return !text || text === "-" || text.toUpperCase() === "N/A";
};

const formatUserSpecificationType = (row) => {
  const spec = String(
    row?.userSpecificationType ?? row?.UserSpecificationType ?? ""
  ).trim();
  return isBlankOrNa(spec) ? NA_DISPLAY : spec;
};

const formatPlantOrRdcNames = (row) => {
  const spec = String(
    row?.userSpecificationType ?? row?.UserSpecificationType ?? ""
  ).trim();
  const plantNames = String(row?.plantNames ?? row?.PlantNames ?? "").trim();
  const rdcNames = String(row?.rdcNames ?? row?.RdcNames ?? "").trim();

  if (/^plant$/i.test(spec)) {
    return isBlankOrNa(plantNames) ? NA_DISPLAY : plantNames;
  }
  if (/^rdc$/i.test(spec)) {
    return isBlankOrNa(rdcNames) ? NA_DISPLAY : rdcNames;
  }
  if (!isBlankOrNa(plantNames)) return plantNames;
  if (!isBlankOrNa(rdcNames)) return rdcNames;
  return NA_DISPLAY;
};

/** Normalize grid geo display text into Master Data Upload tokens (ALL / blank). */
const toMasterGeoToken = (value) => {
  const s = String(value ?? "").trim();
  if (!s || s === "-" || s === "—" || /^n\/?a$/i.test(s)) return "";
  if (/^all countries$/i.test(s) || /^all zones$/i.test(s) || /^all regions$/i.test(s)) {
    return "ALL";
  }
  if (/^all territories$/i.test(s) || /^multiple selected$/i.test(s)) return "ALL";
  if (/^all$/i.test(s)) return "ALL";
  return s;
};

const splitMasterTokens = (value) => {
  const raw = toMasterGeoToken(value);
  if (!raw) return [];
  if (raw === "ALL") return ["ALL"];
  return [
    ...new Set(
      raw
        .split(/[,;]/)
        .map((x) => toMasterGeoToken(x))
        .filter(Boolean)
    ),
  ];
};

const pickMasterGeoAt = (tokens, index) => {
  if (!tokens.length) return "";
  if (tokens.length === 1) return tokens[0];
  return tokens[index] || tokens[tokens.length - 1] || "";
};

const resolveMasterUserType = (row) => {
  const raw = String(
    row?.userSpecificationType ?? row?.UserSpecificationType ?? ""
  ).trim();
  if (/^plant$/i.test(raw)) return "Plant";
  if (/^rdc$/i.test(raw)) return "RDC";
  if (/^bcm$/i.test(raw)) return "BCM";
  if (/^cpd$/i.test(raw) || /^loghead$/i.test(raw)) return "CPD";
  return "General";
};

/**
 * Expand a grid user into Master Data Upload row(s)
 * (Employee_code … Active — same layout as Add User → Master Data Upload).
 */
const buildMasterDataUploadRowsFromUser = (user) => {
  const userType = resolveMasterUserType(user);
  const isPlant = userType === "Plant";
  const isRdc = userType === "RDC";
  const plantOrRdc = formatPlantOrRdcNames(user);
  const plantTokens =
    isPlant || isRdc
      ? splitMasterTokens(plantOrRdc === NA_DISPLAY ? "" : plantOrRdc)
      : [];

  const base = {
    employeeCode: user.employeeCode || user.EmployeeCode || "",
    fullName: user.fullName || user.FullName || "",
    roleName: user.userTypeName || user.UserTypeName || "",
    userType,
    email: user.email || user.Email || "",
    market:
      user.marketName ||
      user.MarketName ||
      user.marketCode ||
      user.MarketCode ||
      "",
    businessVertical:
      user.bvName ||
      user.BvName ||
      user.catgName ||
      user.CatgName ||
      user.catgCode ||
      user.bvCode ||
      "",
    status: user.isActive || user.IsActive ? "Active" : "Inactive",
  };

  if (isPlant || isRdc) {
    const plants = plantTokens.length ? plantTokens : [""];
    return plants.map((plant) => ({
      ...base,
      country: "",
      zone: "",
      region: "",
      territory: "",
      plant,
    }));
  }

  const countries = splitMasterTokens(
    formatUserGeoColumnDisplay(user, user.countryName, user.countryCode)
  );
  const zones = splitMasterTokens(
    formatUserGeoColumnDisplay(user, user.zoneName, user.zoneCode)
  );
  const regions = splitMasterTokens(
    formatUserGeoColumnDisplay(user, user.regionName, user.regionCode)
  );
  const territories = splitMasterTokens(
    formatUserTerritoryColumnDisplay(
      user,
      user.territoryCode || user.TerritoryCode,
      user.territoryName || user.TerritoryName
    )
  );
  const bvTokens = splitMasterTokens(base.businessVertical);
  const bvs = bvTokens.length ? bvTokens : [base.businessVertical || ""];

  const geoCount = Math.max(
    countries.length,
    zones.length,
    regions.length,
    territories.length,
    1
  );

  const rows = [];
  for (const bv of bvs) {
    for (let i = 0; i < geoCount; i++) {
      let country = pickMasterGeoAt(countries, i);
      const zone = pickMasterGeoAt(zones, i);
      const region = pickMasterGeoAt(regions, i);
      const territory = pickMasterGeoAt(territories, i);
      if (!country && (zone || region || territory)) country = "ALL";
      rows.push({
        ...base,
        businessVertical: bv,
        country,
        zone,
        region,
        territory,
        plant: "",
      });
    }
  }
  return rows;
};

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
  });
  const {
    filterExpanded,
    setFilterExpanded,
    filters: geoFilters,
    setFilters: setGeoFilters,
    marketOptions,
    bvOptions,
    roleOptions,
    countryOptions,
    zoneOptions,
    regionOptions,
    territoryOptions,
    geoLoading,
    isExportCascade,
    exportCountryOptions,
    isGeoAllFilterValue,
    isRoleAllFilterValue,
    handleGeoFilterChange,
    handleMarketChange,
    handleBvChange,
    handleRoleChange,
    resetFilters: resetGeoFilters,
    toQueryParams,
  } = useUserManagementFilters();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const tableRef = React.useRef(null);
  const dataTableInstanceRef = React.useRef(null);
  const statusSelectRef = React.useRef(null);

  // Dynamic permission checks for this route.
  // Supports routes that map to multiple MenuCodes (e.g., USER_VIEW + USER_MANAGE).
  const routePermissions = getRoutePermissions(location.pathname);
  const { canView, canAdd, canEdit, canDelete } = routePermissions;

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this page.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/dashboard");
      });
      return;
    }
    // Single load on mount / when returning to this route (key remount or pathname).
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, location.key]);

  // Initialize Select2 for Status dropdown
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);

      // Destroy existing Select2 instance if any
      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      // Initialize Select2
      $select.select2({
        width: "100%",
        minimumResultsForSearch: Infinity, // Disable search for small dropdown
      });

      // Handle change event
      $select.on("change", function (e) {
        handleFilterChange("status", e.target.value);
      });

      // Cleanup on unmount
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update Select2 value when filters.status changes (without triggering our change handler)
  useEffect(() => {
    if (statusSelectRef.current && $(statusSelectRef.current).data("select2")) {
      const $select = $(statusSelectRef.current);
      const currentValue = $select.val();
      if (currentValue !== filters.status) {
        // Temporarily remove our change handler, update value, then reattach
        $select.off("change");
        $select.val(filters.status).trigger("change.select2");
        $select.on("change", function (e) {
          handleFilterChange("status", e.target.value);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // Initialize / re-init DataTable when users change
  useEffect(() => {
    if (!tableRef.current) return;

    // Destroy previous instance
    if (dataTableInstanceRef.current) {
      disposeUserGeoTooltips(tableRef.current);
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    // Initialize DataTable plugin
    try {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: users,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        deferRender: true,
        pageLength: 50,
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) => (meta.row + 1).toString(),
            className: "text-center",
          },
          {
            title: "Employee Code",
            data: "employeeCode",
            render: (d, type, row) => {
              const codeText = d || "-";
              if (type !== "display") return codeText;
              const userCode = row.userIdCode || row.userId;
              if (!canEdit || !userCode || codeText === "-") {
                return escapeHtml(codeText);
              }
              return `<a href="#" class="employee-code-link edit-user" data-code="${escapeHtml(
                userCode
              )}" title="Edit User">${escapeHtml(codeText)}</a>`;
            },
          },
          {
            title: "AD ID",
            data: "userName",
            render: (d) => d || "-",
          },
          {
            title: "User Full Name",
            data: "fullName",
            render: (d) => d || "-",
          },
          {
            title: "Employee email ID",
            data: "email",
            render: (d) => d || "-",
          },
          {
            title: "Mobile Number",
            data: "phoneNumber",
            render: (d) => d || "-",
          },
          {
            title: "Role",
            data: "userTypeName",
            render: (d) => d || "-",
          },
          {
            title: "Market",
            data: "marketName",
            render: (d, type, row) => d || row.marketCode || "-",
          },
          {
            title: "Country",
            data: "countryName",
            render: (d, type, row) =>
              renderGeoTooltipCell(
                formatUserGeoColumnDisplay(row, d, row.countryCode),
                type
              ),
          },
          {
            title: "Zone",
            data: "zoneName",
            // Search matches Zone text only (e.g. "West"), not Region names.
            render: (d, type, row) => {
              const zoneText = formatUserGeoColumnDisplay(row, d, row.zoneCode);
              if (type === "filter" || type === "sort" || type === "type") {
                return zoneText;
              }
              return renderGeoTooltipCell(zoneText, type);
            },
          },
          {
            title: "Region",
            data: "regionName",
            // Exclude from DataTables global Search so "West" does not match "WEST BENGAL"
            // (East-zone users). Use the Zone filter above for zone-only results.
            searchable: false,
            render: (d, type, row) =>
              renderGeoTooltipCell(
                formatUserGeoColumnDisplay(row, d, row.regionCode),
                type
              ),
          },
          {
            title: "Territory",
            data: "territoryName",
            render: (d, type, row) =>
              renderGeoTooltipCell(
                formatUserTerritoryColumnDisplay(
                  row,
                  row.territoryCode || row.TerritoryCode,
                  d || row.territoryName || row.TerritoryName
                ),
                type
              ),
          },
          {
            title: "Business Vertical",
            data: "bvName",
            render: (d, type, row) =>
              renderGeoTooltipCell(d || row.catgName || "-", type),
          },
          {
            title: "User Type",
            data: "userSpecificationType",
            render: (d, type, row) =>
              renderGeoTooltipCell(formatUserSpecificationType(row), type),
          },
          {
            title: "Plant / RDC",
            data: null,
            render: (d, type, row) =>
              renderGeoTooltipCell(formatPlantOrRdcNames(row), type),
          },
          {
            title: "Created On",
            data: "createdOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Status",
            data: null,
            orderable: false,
            render: (d) => {
              const isActive = d.isActive === true || d.isActive === 1;
              const toggleClass = isActive ? "active" : "inactive";
              const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
              if (!canEdit) {
                return `<div class="d-flex justify-content-center">
                  <span
                    title="${isActive ? "Active" : "Inactive"}"
                    style="background: transparent; 
                           border: none; 
                           padding: 0;
                           font-size: 24px;
                           color: ${isActive ? "#4caf50" : "#9e9e9e"};
                           cursor: default;
                           transition: all 0.3s ease;">
                    <i class="bi ${toggleIcon}"></i>
                  </span>
                </div>`;
              }
              return `<div class="d-flex justify-content-center">
                  <button class="btn btn-sm status-toggle ${toggleClass}" 
                    data-code="${d.userIdCode || d.userId}" 
                    data-active="${isActive}"
                    title="${
                      isActive ? "Click to deactivate" : "Click to activate"
                    }"
                    style="background: transparent; 
                           border: none; 
                           padding: 0;
                           font-size: 24px;
                           color: ${isActive ? "#4caf50" : "#9e9e9e"};
                           cursor: pointer;
                           transition: all 0.3s ease;">
                    <i class="bi ${toggleIcon}"></i>
                  </button>
                </div>`;
            },
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            searchable: false,
            render: (d) => {
              const code = d.userIdCode || d.userId;
              const editBtn = canEdit
                ? `<button class="btn btn-sm edit-user" data-code="${code}" title="Edit User"
                    style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
                           border: none; 
                           color: white; 
                           padding: 8px 10px; 
                           border-radius: 6px; 
                           box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
                           transition: all 0.3s ease;
                           font-weight: 500;
                           display: flex;
                           align-items: center;
                           justify-content: center;
                           min-width: 36px;
                           height: 36px;">
                    <i class="bi bi-pencil-fill"></i>
                  </button>`
                : "";

              const deleteBtn = canDelete
                ? `<button class="btn btn-sm delete-user" data-code="${code}" title="Delete User"
                    style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); 
                           border: none; 
                           color: white; 
                           padding: 8px 10px; 
                           border-radius: 6px; 
                           box-shadow: 0 2px 8px rgba(245, 87, 108, 0.4);
                           transition: all 0.3s ease;
                           font-weight: 500;
                           display: flex;
                           align-items: center;
                           justify-content: center;
                           min-width: 36px;
                           height: 36px;">
                    <i class="bi bi-trash-fill"></i>
                  </button>`
                : "";

              const content = `${editBtn}${deleteBtn}`;
              return `<div class="d-flex justify-content-center gap-2">${
                content || `<span class="text-muted">-</span>`
              }</div>`;
            },
          },
        ],
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(tableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(tableRef.current).closest(".dt-scroll");
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(tableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
        drawCallback: function () {
          try {
            initUserGeoTooltips(tableRef.current);
          } catch (error) {
            console.error("Error initializing user geo tooltips:", error);
          }
        },
      });

      // Add hover effects for glossy action buttons only (not employee-code links)
      $(tableRef.current)
        .off("mouseenter", "button.edit-user, .delete-user")
        .on("mouseenter", "button.edit-user, .delete-user", function () {
          $(this).css({
            transform: "translateY(-2px)",
            boxShadow: $(this).hasClass("edit-user")
              ? "0 4px 12px rgba(102, 126, 234, 0.6)"
              : "0 4px 12px rgba(245, 87, 108, 0.6)",
          });
        })
        .off("mouseleave", "button.edit-user, .delete-user")
        .on("mouseleave", "button.edit-user, .delete-user", function () {
          $(this).css({
            transform: "translateY(0)",
            boxShadow: $(this).hasClass("edit-user")
              ? "0 2px 8px rgba(102, 126, 234, 0.4)"
              : "0 2px 8px rgba(245, 87, 108, 0.4)",
          });
        })
        .off("mousedown", "button.edit-user, .delete-user")
        .on("mousedown", "button.edit-user, .delete-user", function () {
          $(this).css({ transform: "translateY(0)" });
        })
        .off("mouseup", "button.edit-user, .delete-user")
        .on("mouseup", "button.edit-user, .delete-user", function () {
          $(this).css({ transform: "translateY(-2px)" });
        });

      // Delegated handlers for edit/delete
      if (canEdit) {
        $(tableRef.current)
          .off("click", ".edit-user")
          .on("click", ".edit-user", function (e) {
            e.preventDefault();
            const code = $(this).data("code");
            if (code) {
              navigate(`/users/edit/${code}`);
            }
          });
      } else {
        $(tableRef.current).off("click", ".edit-user");
      }

      if (canDelete) {
        $(tableRef.current)
          .off("click", ".delete-user")
          .on("click", ".delete-user", function () {
            const code = $(this).data("code");
            if (code) {
              handleDelete(code);
            }
          });
      } else {
        $(tableRef.current).off("click", ".delete-user");
      }

      // Status toggle handler
      if (canEdit) {
        $(tableRef.current)
          .off("click", ".status-toggle")
          .on("click", ".status-toggle", function () {
            const code = $(this).data("code");
            const currentActive =
              $(this).data("active") === true ||
              $(this).data("active") === "true" ||
              $(this).data("active") === 1;
            const newActive = !currentActive;
            handleStatusToggle(code, currentActive, newActive);
          });
      } else {
        $(tableRef.current).off("click", ".status-toggle");
      }
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      disposeUserGeoTooltips(tableRef.current);
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, canEdit, canDelete]);

  const fetchUsers = async (
    search = filters.search,
    status = filters.status,
    geoQuery = null
  ) => {
    setLoading(true);
    try {
      const geo = geoQuery || toQueryParams();
      const response = await userAPI.getAll({
        search: search || undefined,
        status: status === "all" ? undefined : status.toLowerCase(),
        ...geo,
      });
      if (response && response.success && response.data) {
        setUsers(Array.isArray(response.data) ? response.data : []);
      } else {
        setUsers([]);
      }
    } catch (e) {
      console.error("Error fetching users:", e);
      setUsers([]);
      Swal.fire("Error", "Failed to load users. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // filtering is performed server-side via fetchUsers

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", status: "all" });
    resetGeoFilters();
    fetchUsers("", "all", {
      marketCode: undefined,
      bvCode: undefined,
      userTypeCode: undefined,
      countryCode: undefined,
      zoneCode: undefined,
      regionCode: undefined,
      territoryCode: undefined,
    });
  };

  const handleStatusToggle = async (userIdCode, currentActive, newActive) => {
    const actionText = newActive ? "activate" : "deactivate";
    const confirmText = newActive
      ? "Are you sure you want to activate this user?"
      : "Are you sure you want to deactivate this user?";
    const confirmButtonText = newActive ? "Yes, activate!" : "Yes, deactivate!";

    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: confirmText,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newActive ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: confirmButtonText,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await userAPI.updateStatus(
          userIdCode,
          newActive,
          modifiedBy
        );

        if (response && response.success) {
          Swal.fire(
            "Success!",
            response.message || `User has been ${actionText}d.`,
            "success"
          );
          fetchUsers();
        } else {
          Swal.fire(
            "Error",
            response.message || `Failed to ${actionText} user`,
            "error"
          );
        }
      } catch (e) {
        console.error(`Error ${actionText}ing user:`, e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            `Failed to ${actionText} user. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleDelete = async (userIdCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await userAPI.delete(userIdCode, modifiedBy);

        if (response && response.success) {
          Swal.fire(
            "Deleted!",
            response.message || "User has been deleted.",
            "success"
          );
          fetchUsers();
        } else {
          Swal.fire(
            "Error",
            response.message || "Failed to delete user",
            "error"
          );
        }
      } catch (e) {
        console.error("Error deleting user:", e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            "Failed to delete user. Please try again.",
          "error"
        );
      }
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, users);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF("landscape");
      doc.setFontSize(16);
      doc.text("User Management Data", 14, 15);

      const tableData = exportRows.map((user, index) => [
        String(index + 1),
        String(user.employeeCode || "-"),
        String(user.userName || "-"),
        String(user.fullName || "-"),
        String(user.email || "-"),
        String(user.phoneNumber || "-"),
        String(user.userTypeName || "-"),
        String(user.marketName || user.marketCode || "-"),
        String(formatUserGeoColumnDisplay(user, user.countryName, user.countryCode)),
        String(formatUserGeoColumnDisplay(user, user.zoneName, user.zoneCode)),
        String(formatUserGeoColumnDisplay(user, user.regionName, user.regionCode)),
        String(
          formatUserTerritoryColumnDisplay(
            user,
            user.territoryCode || user.TerritoryCode,
            user.territoryName || user.TerritoryName
          )
        ),
        String(user.bvName || user.catgName || user.catgCode || user.bvCode || "-"),
        String(formatUserSpecificationType(user)),
        String(formatPlantOrRdcNames(user)),
        String(formatDateTime(user.createdOn) || "-"),
        user.isActive ? "Active" : "Inactive",
      ]);

      // Use autoTable function (imported directly)
      autoTable(doc, {
        head: [[
          "SNo.",
          "Employee Code",
          "AD ID",
          "User Full Name",
          "Email",
          "Mobile Number",
          "Role",
          "Market",
          "Country",
          "Zone",
          "Region",
          "Territory",
          "Business Vertical",
          "User Type",
          "Plant / RDC",
          "Created On",
          "Status",
        ]],
        body: tableData,
        startY: 25,
        styles: { fontSize: 6 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { top: 25 },
      });

      doc.save(`User_Management_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}. Please check the browser console for details.`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, users);
    try {
      const headers = ["SNo.", "Employee Code", "AD ID", "User Full Name", "Email", "Mobile Number", "Role", "Market", "Country", "Zone", "Region", "Territory", "Business Vertical", "User Type", "Plant / RDC", "Created On", "Status"];
      const csvData = exportRows.map((user, index) => [
        index + 1,
        user.employeeCode || "",
        user.userName || "",
        user.fullName || "",
        user.email || "",
        user.phoneNumber || "",
        user.userTypeName || "",
        user.marketName || user.marketCode || "",
        formatUserGeoColumnDisplay(user, user.countryName, user.countryCode),
        formatUserGeoColumnDisplay(user, user.zoneName, user.zoneCode),
        formatUserGeoColumnDisplay(user, user.regionName, user.regionCode),
        formatUserTerritoryColumnDisplay(
          user,
          user.territoryCode || user.TerritoryCode,
          user.territoryName || user.TerritoryName
        ),
        user.bvName || user.catgName || user.catgCode || user.bvCode || "",
        formatUserSpecificationType(user),
        formatPlantOrRdcNames(user),
        formatDateTime(user.createdOn),
        user.isActive ? "Active" : "Inactive",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `User_Management_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, users);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("User Management");

      // Add title
      worksheet.mergeCells("A1:Q1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "User Management Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // Add headers
      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Employee Code", "AD ID", "User Full Name", "Email", "Mobile Number", "Role", "Market", "Country", "Zone", "Region", "Territory", "Business Vertical", "User Type", "Plant / RDC", "Created On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      // Add data
      exportRows.forEach((user, index) => {
        const row = worksheet.addRow([
          index + 1,
          user.employeeCode || "",
          user.userName || "",
          user.fullName || "",
          user.email || "",
          user.phoneNumber || "",
          user.userTypeName || "",
          user.marketName || user.marketCode || "",
          formatUserGeoColumnDisplay(user, user.countryName, user.countryCode),
          formatUserGeoColumnDisplay(user, user.zoneName, user.zoneCode),
          formatUserGeoColumnDisplay(user, user.regionName, user.regionCode),
          formatUserTerritoryColumnDisplay(
            user,
            user.territoryCode || user.TerritoryCode,
            user.territoryName || user.TerritoryName
          ),
          user.bvName || user.catgName || user.catgCode || user.bvCode || "",
          formatUserSpecificationType(user),
          formatPlantOrRdcNames(user),
          formatDateTime(user.createdOn),
          user.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      // Set column widths
      worksheet.columns.forEach((column) => {
        column.width = 18;
      });

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `User_Management_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Export in Master Data Upload Excel structure (same as Add User → Master Data Upload)
  const exportToMasterDataExcel = async () => {
    const exportRows = getDataTableExportRows(dataTableInstanceRef, users);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("MikasaPLY");
      const headerRow = worksheet.getRow(1);
      MASTER_USER_EXCEL_HEADERS.forEach((h, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 11 };
        cell.alignment = { vertical: "middle" };
      });

      let written = 0;
      exportRows.forEach((user) => {
        buildMasterDataUploadRowsFromUser(user).forEach((row) => {
          written += 1;
          const excelRow = worksheet.getRow(written + 1);
          const vals = [
            row.employeeCode,
            row.fullName,
            row.roleName,
            row.userType,
            row.email,
            row.market,
            row.country,
            row.zone,
            row.region,
            row.territory,
            row.plant,
            row.businessVertical,
            row.status,
          ];
          vals.forEach((v, i) => {
            excelRow.getCell(i + 1).value = v;
          });
        });
      });

      const widths = [15, 28, 17, 12, 42, 12, 8, 10, 16, 24, 14, 36, 12];
      worksheet.columns.forEach((col, i) => {
        col.width = widths[i] || 18;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Users_MasterDataUpload_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting master data Excel:", error);
      Swal.fire("Error", "Failed to export Master Data Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "masterExcel") {
      exportToMasterDataExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-user-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              User Management
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage users, roles, and statuses.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel (Grid)
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("masterExcel")}>
              Excel (Master Data Upload)
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/users/add")}
              sx={{
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                fontSize: "11px",
                boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
                borderRadius: "4px",
                textTransform: "uppercase",
                padding: "8px 24px",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                  boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
              title="Add User"
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      <div className="card-panel mb-3 filter-panel" style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}>
        <div
          className={`section-heading d-flex align-items-center gap-2 ${
            filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
          }`}
          style={{ marginBottom: filterExpanded ? "14px" : 0, cursor: "pointer" }}
          onClick={() => setFilterExpanded(!filterExpanded)}
        >
          <FilterListIcon
            fontSize="small"
            className="text-muted"
            style={{ width: "18px", height: "18px", flexShrink: 0, marginTop: "2px" }}
          />
          <span>Filter</span>
          {filterExpanded ? (
            <ExpandLessIcon fontSize="small" className="text-muted ms-auto" style={{ width: "18px", height: "18px" }} />
          ) : (
            <ExpandMoreIcon fontSize="small" className="text-muted ms-auto" style={{ width: "18px", height: "18px" }} />
          )}
        </div>

        {filterExpanded && (
        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Market</label>
              <select
                className="form-select form-control-light filter-select text-start"
                value={geoFilters.market || ""}
                disabled={!marketOptions.length}
                onChange={(e) => handleMarketChange(e.target.value || "")}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="">All</option>
                {marketOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.text}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Business Vertical</label>
              <CustomMultiSelect
                options={bvOptions}
                value={geoFilters.bv || []}
                onChange={handleBvChange}
                placeholder="Select Business Vertical"
                showSelectAll={!isGeoAllFilterValue(geoFilters.bv)}
              />
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Role</label>
              <CustomMultiSelect
                options={roleOptions}
                value={geoFilters.role || []}
                onChange={handleRoleChange}
                placeholder="Select Role"
                showSelectAll={!isRoleAllFilterValue(geoFilters.role)}
              />
            </div>

            {(isExportCascade
              ? ["zone", "country", "region", "territory"]
              : ["country", "zone", "region", "territory"]
            ).map((geoKey) => {
              const countryIsAll = isGeoAllFilterValue(geoFilters.country);
              const zoneIsAll = isGeoAllFilterValue(geoFilters.zone);
              const regionIsAll = isGeoAllFilterValue(geoFilters.region);
              if (geoKey === "country") {
                const countryList = isExportCascade ? exportCountryOptions : countryOptions;
                return (
                  <div key="country" className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">Country</label>
                    <CustomMultiSelect
                      options={countryList}
                      value={geoFilters.country || []}
                      onChange={(selected) =>
                        handleGeoFilterChange("country", selected, countryList)
                      }
                      placeholder={
                        isExportCascade
                          ? geoFilters.zone?.length
                            ? "Select Country"
                            : "Select zone first"
                          : "Select Country"
                      }
                      disabled={isExportCascade && !geoFilters.zone?.length}
                      loading={geoLoading.country}
                      loadingText="Loading countries..."
                      showSelectAll={!countryIsAll}
                    />
                  </div>
                );
              }
              if (geoKey === "zone") {
                return (
                  <div key="zone" className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">Zone</label>
                    <CustomMultiSelect
                      options={zoneOptions}
                      value={geoFilters.zone || []}
                      onChange={(selected) =>
                        handleGeoFilterChange("zone", selected, zoneOptions)
                      }
                      placeholder={
                        isExportCascade
                          ? "Select zone(s)"
                          : geoFilters.country?.length
                            ? "Select zone(s)"
                            : "Select country first"
                      }
                      disabled={!isExportCascade && !geoFilters.country?.length}
                      loading={geoLoading.zone}
                      loadingText="Loading zones..."
                      showSelectAll={!zoneIsAll}
                    />
                  </div>
                );
              }
              if (geoKey === "region") {
                const regionReady = isExportCascade
                  ? geoFilters.zone?.length && geoFilters.country?.length
                  : geoFilters.zone?.length;
                return (
                  <div key="region" className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">Region</label>
                    <CustomMultiSelect
                      options={regionOptions}
                      value={geoFilters.region || []}
                      onChange={(selected) =>
                        handleGeoFilterChange("region", selected, regionOptions)
                      }
                      placeholder={
                        regionReady
                          ? "Select region(s)"
                          : isExportCascade
                            ? "Select country first"
                            : "Select zone first"
                      }
                      disabled={!regionReady}
                      loading={geoLoading.region}
                      loadingText="Loading regions..."
                      showSelectAll={!regionIsAll}
                    />
                  </div>
                );
              }
              return (
                <div key="territory" className="col-12 col-md-6 col-lg-3">
                  <label className="input-label d-block mb-1 text-start">Territory</label>
                  <CustomMultiSelect
                    options={territoryOptions}
                    value={geoFilters.territory || []}
                    onChange={(selected) =>
                      handleGeoFilterChange("territory", selected, territoryOptions)
                    }
                    placeholder={
                      geoFilters.region?.length
                        ? "Select territory(ies)"
                        : "Select region first"
                    }
                    disabled={!geoFilters.region?.length}
                    loading={geoLoading.territory}
                    loadingText="Loading territories..."
                    showSelectAll={!isGeoAllFilterValue(geoFilters.territory)}
                  />
                </div>
              );
            })}

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start" style={{ visibility: "hidden" }}>
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={() => fetchUsers()}
                  style={{
                    background: "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                  style={{
                    background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>


      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {loading && users.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading users...</Typography>
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No users found. Click "Add User" to create a new user.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default UserManagement;
